import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SiAppstore, SiGoogleplay } from 'react-icons/si';
import { api } from '../../api/client';
import { API_BASE_URL } from '../../config/api';
import sweetLassi from '../../assets/images/sweet-lassi.png';
import southThali from '../../assets/images/south-india-thali.png';
import modernKitchen from '../../assets/images/modern-kitchen.png';
import ecoDesignTransparent from '../../assets/images/eco-design-transparent-v3.png';
import getStartedPhoneImg from '../../assets/images/get-started-phone.png';
import miniThaliPlans from '../../assets/images/mini-thali-plans.png';
import flexiblePlan1Month from '../../assets/images/flexible-plan-1month.png';
import flexiblePlan2Month from '../../assets/images/flexible-plan-2month.png';
import flexiblePlan3Month from '../../assets/images/flexible-plan-3month.png';
import './HeroSlider.css';

const FALLBACK_SLIDES = [
  {
    tag: 'Fresh Meal',
    title: 'Healthy Meals, Delivered Fresh',
    subtitle: 'Organic & Regular',
    description:
      'Simple, tasty and balanced meals for Weight loss, Diabetic and PCOD needs, freshly made with trusted ingredients and zero preservatives, food colors, tasting salt, palm or sunflower oil.',
    image: southThali,
    bgGradient: 'linear-gradient(135deg, #fde8c0 0%, #f5a800 60%, #c96000 100%)',
    primaryLink: '/our-menu',
    primaryBtnText: 'Order Now',
    secondaryLink: '/meal-plans#subscription-builder',
    secondaryBtnText: 'Meal plans',
  },
  {
    tag: 'Flexible Plans',
    title: 'SUBSCRIPTION BENEFITS',
    subtitle: 'Flexible Planning',
    description:
      'Consult our Nutritionist to design your Meal. Unique and Fresh meals everyday. Delivered at your doorstep.',
    image: sweetLassi,
    collageImages: [flexiblePlan1Month, flexiblePlan2Month, flexiblePlan3Month],
    bgGradient: 'linear-gradient(135deg, #a8d5b8 0%, #52a876 60%, #2d6a4f 100%)',
    primaryLink: '/meal-plans',
    primaryBtnText: 'Monthly Plans',
    secondaryLink: '/contact',
    secondaryBtnText: 'Connect',
  },
  {
    tag: 'Mini Thali Plans',
    title: 'AFFORDABLE DAILY MEAL PLANS',
    subtitle: 'Save time, eat healthy every day',
    description:
      'Quick, budget-friendly mini thali options for everyday nutrition without compromising taste.',
    image: miniThaliPlans,
    bgGradient: 'linear-gradient(135deg, #1a1a2e 0%, #c62828 60%, #8b0000 100%)',
    primaryLink: '/meal-plans#subscription-builder',
    primaryBtnText: 'Order Now',
    secondaryLink: '/meal-plans#subscription-builder',
    secondaryBtnText: 'Subscribe Now',
  },
  {
    tag: 'Get Started',
    title: 'GET STARTED',
    subtitle: 'Download our APP',
    description:
      'Download the app to explore plans, order meals, and manage your subscriptions with ease.',
    image: getStartedPhoneImg,
    bgGradient: 'linear-gradient(135deg, #1f2937 0%, #374151 60%, #111827 100%)',
    primaryExternal: 'https://play.google.com/store/apps/details?id=com.digirasa.client',
    primaryBtnText: 'Google Play',
    primaryIcon: 'play',
    secondaryExternal: 'https://apps.apple.com/in/app/digirasa/id6736368836',
    secondaryBtnText: 'App Store',
    secondaryIcon: 'apple',
  },
  {
    tag: 'Eco Design',
    title: 'OUR DESIGN',
    subtitle: 'Ecofriendly & Biodegradable Packing',
    description:
      'NO PLASTIC. Thoughtfully packed in ecofriendly, biodegradable materials for a cleaner and greener future.',
    image: ecoDesignTransparent,
    bgGradient: 'linear-gradient(135deg, #0f172a 0%, #14532d 60%, #166534 100%)',
  },
];

function mapApiSlide(row) {
  const collage = Array.isArray(row?.collage_absolute_urls)
    ? row.collage_absolute_urls.filter(Boolean)
    : [];
  return {
    tag: row.tag || '',
    title: row.title || '',
    subtitle: row.subtitle || '',
    description: row.description || '',
    image: row.absolute_image_url || '',
    collageImages: collage.length === 3 ? collage : undefined,
    bgGradient: row.bg_gradient || FALLBACK_SLIDES[0].bgGradient,
    primaryLink: row.primary_link || undefined,
    primaryBtnText: row.primary_btn_text || undefined,
    primaryExternal: row.primary_external || undefined,
    primaryIcon: row.primary_icon || undefined,
    secondaryLink: row.secondary_link || undefined,
    secondaryBtnText: row.secondary_btn_text || undefined,
    secondaryExternal: row.secondary_external || undefined,
    secondaryIcon: row.secondary_icon || undefined,
  };
}

const HeroSlider = () => {
  const [slides, setSlides] = useState(FALLBACK_SLIDES);
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [textKey, setTextKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`${API_BASE_URL}/api/web-hero-slides/active`);
        const rows = Array.isArray(data?.data) ? data.data : [];
        if (!cancelled && rows.length) {
          setSlides(rows.map(mapApiSlide));
          setCurrent(0);
        }
      } catch {
        /* keep the designed fallback slides */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goTo = useCallback(
    (index) => {
      if (animating || !slides.length) return;
      setAnimating(true);
      setCurrent(index);
      setTextKey((k) => k + 1);
      setTimeout(() => setAnimating(false), 600);
    },
    [animating, slides.length],
  );

  const next = useCallback(
    () => goTo((current + 1) % slides.length),
    [current, goTo, slides.length],
  );
  const prev = useCallback(
    () => goTo((current - 1 + slides.length) % slides.length),
    [current, goTo, slides.length],
  );

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next, slides.length]);

  const slide = slides[current] || slides[0];
  if (!slide) return null;

  return (
    <section className="hero" style={{ background: slide.bgGradient }}>
      <div className="hero__bg" style={{ backgroundImage: `url(${modernKitchen})` }} />
      <div className="hero__overlay" />
      <div className="container hero__container">
        <div key={textKey} className="hero__content hero__content--animating">
          <div className="hero__text">
            {slide.tag ? <span className="section-tag hero__tag">{slide.tag}</span> : null}
            <h1 className="hero__title">{slide.title}</h1>
            {slide.subtitle ? <h2 className="hero__subtitle">{slide.subtitle}</h2> : null}
            {slide.description ? <p className="hero__desc">{slide.description}</p> : null}
          </div>
          {slide.primaryBtnText || slide.secondaryBtnText ? (
            <div className="hero__btns">
              {slide.primaryLink ? (
                <Link to={slide.primaryLink} className="btn-primary">
                  {slide.primaryBtnText}
                </Link>
              ) : null}
              {slide.primaryExternal ? (
                <a
                  href={slide.primaryExternal}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={
                    slide.primaryIcon
                      ? 'btn-primary hero__store-pill hero__store-link'
                      : 'btn-primary hero__store-link'
                  }
                >
                  {slide.primaryIcon === 'play' ? (
                    <SiGoogleplay className="hero__btn-icon" />
                  ) : null}
                  {slide.primaryIcon === 'apple' ? (
                    <SiAppstore className="hero__btn-icon" />
                  ) : null}
                  {slide.primaryIcon ? (
                    <span className="hero__store-text">
                      <span className="hero__store-kicker">
                        {slide.primaryIcon === 'apple' ? 'Download on the' : 'GET IT ON'}
                      </span>
                      <span className="hero__store-name">{slide.primaryBtnText}</span>
                    </span>
                  ) : (
                    slide.primaryBtnText
                  )}
                </a>
              ) : null}
              {slide.secondaryLink ? (
                <Link to={slide.secondaryLink} className="btn-secondary">
                  {slide.secondaryBtnText}
                </Link>
              ) : null}
              {slide.secondaryExternal ? (
                <a
                  href={slide.secondaryExternal}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={
                    slide.secondaryIcon
                      ? 'btn-secondary hero__store-pill hero__store-link'
                      : 'btn-secondary hero__store-link'
                  }
                >
                  {slide.secondaryIcon === 'apple' ? (
                    <SiAppstore className="hero__btn-icon" />
                  ) : null}
                  {slide.secondaryIcon === 'play' ? (
                    <SiGoogleplay className="hero__btn-icon" />
                  ) : null}
                  {slide.secondaryIcon ? (
                    <span className="hero__store-text">
                      <span className="hero__store-kicker">
                        {slide.secondaryIcon === 'apple' ? 'Download on the' : 'GET IT ON'}
                      </span>
                      <span className="hero__store-name">{slide.secondaryBtnText}</span>
                    </span>
                  ) : (
                    slide.secondaryBtnText
                  )}
                </a>
              ) : null}
            </div>
          ) : null}
        </div>

        {slide.collageImages ? (
          <div
            className={`hero__image-wrap hero__image-wrap--plans${animating ? ' hero__image-wrap--animating' : ''}`}
          >
            <div className="hero__plans-collage">
              <img
                src={slide.collageImages[0]}
                alt=""
                className="hero__plans-item hero__plans-item--left"
              />
              <img
                src={slide.collageImages[1]}
                alt=""
                className="hero__plans-item hero__plans-item--right"
              />
              <img
                src={slide.collageImages[2]}
                alt=""
                className="hero__plans-item hero__plans-item--bottom"
              />
            </div>
          </div>
        ) : slide.image ? (
          <div className={`hero__image-wrap${animating ? ' hero__image-wrap--animating' : ''}`}>
            <img src={slide.image} alt={slide.title} className="hero__image" />
          </div>
        ) : null}
      </div>

      {slides.length > 1 ? (
        <>
          <button className="hero__arrow hero__arrow--prev" onClick={prev} aria-label="Previous slide">
            &#8592;
          </button>
          <button className="hero__arrow hero__arrow--next" onClick={next} aria-label="Next slide">
            &#8594;
          </button>
          <div className="hero__dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`hero__dot${i === current ? ' hero__dot--active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
};

export default HeroSlider;
